/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState } from 'react'
import Styles from './styles.module.sass'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { joiResolver } from '@hookform/resolvers/joi'
import {
  schema
} from './register-form.schemas'
import AdditionalMetadataExtracted from '../AdditionalMetadataExtracted'
import FileInfo from '../FileInfo'
import CertifyAuthorized from './components/CertifyAuthorized'
import AssetMetadata from './components/AssetMetadata'
import { assetsUrls } from '@/configs/urls'
import {
  Box,
  Button,
  Flex,
  useBreakpointValue,
  useToast
} from '@chakra-ui/react'
import { useResponsive } from '@/hooks/useResponsive'
import Royalties from '@/pages/Assets/pages/RegisterAsset/components/Royalties'
import Ownership from '@/pages/Assets/pages/RegisterAsset/components/Ownership'

export const RegisterForm = ({
  cancelUrl,
  onSubmit,
  currentCreators,
  currentMetadata,
  currentPublishers,
  currentTitle,
  currentDescription,
  selectedFile,
  fileType
}) => {
  const [creators, setCreators] = useState(currentCreators)
  const [publishers, setPublishers] = useState(currentPublishers)
  const [creatorsError, setCreatorsError] = useState('')
  const [publishersError, setPublishersError] = useState('')
  const toast = useToast()
  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    setValue,
    watch
  } = useForm({
    defaultValues: {
      title: currentTitle,
      description: currentDescription
    },
    resolver: joiResolver(schema)
  })
  const navigate = useNavigate()
  const curOnSubmit = async data => {
    const { ownership, attestationType } = data

    setCreatorsError('')
    setPublishersError('')

    if (ownership === 'PUBLISHER' && attestationType === 'AUTHORIZED_AGENT') {
      if (!publishers || publishers.length === 0) {
        const errorMsg = 'At least one publisher is required when ownership is Publisher and you are acting as an Authorized Agent.'
        setPublishersError(errorMsg)
        toast({
          title: 'Validation Error',
          description: errorMsg,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top'
        })
        return
      }
    } else {
      if (!creators || creators.length === 0) {
        const errorMsg = 'At least one creator is required.'
        setCreatorsError(errorMsg)
        toast({
          title: 'Validation Error',
          description: errorMsg,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top'
        })
        return
      }
    }

    onSubmit && onSubmit({ data, creators, publishers })
  }
  const onDeleteFile = () => {
    navigate(`/${assetsUrls.base}/${assetsUrls.new}`)
  }
  const fileName = selectedFile ? selectedFile.name : 'myfile.epub'
  const selectFiletype = fileType
    ? [{
        label: fileType?.toUpperCase(),
        value: fileType
      }]
    : []
  const formPadding = useBreakpointValue({
    base: '15px',
    md: '50px'
  })
  const { flexGap } = useResponsive()

  return (
    <Box
      className={Styles.form}
      padding={formPadding}
    >
      <form
        onSubmit={handleSubmit(curOnSubmit)}
      >
        <FileInfo
          fileName={fileName}
          fileType={fileType}
          fileValidated={true}
          metadataExtracted={!!selectedFile}
          onDeleteFile={onDeleteFile}
          selectedFile={selectedFile}
        />
        <AssetMetadata
          control={control}
          errors={errors}
          fileType={selectFiletype}
          register={register}
          currentCreators={currentCreators}
          currentPublishers={currentPublishers}
          setCreators={setCreators}
          setPublishers={setPublishers}
          creatorsError={creatorsError}
          publishersError={publishersError}
          setCreatorsError={setCreatorsError}
          setPublishersError={setPublishersError}
        />
        <AdditionalMetadataExtracted
          metadata={currentMetadata}
          register={register}
          control={control}
        />
        <Ownership register={register} errors={errors} />
        <CertifyAuthorized
          control={control}
          currentCreators={creators}
          errors={errors}
          register={register}
          userNickname="TEST USER"
          setValue={setValue}
          watch={watch}
          hasCreators={!!creators?.length}
        />
        <Royalties
          errors={errors}
          register={register}
        />
        <Flex gap={flexGap}>
          <Button
            className="is-pulled-left"
            type="submit"
          >
            Register
          </Button>
          <Button
            className={`${Styles.cancelBtn} is-pulled-right`}
            as={Link}
            to={cancelUrl}
            colorScheme='gray'
          >
            Cancel
          </Button>
        </Flex>
      </form>
    </Box>
  )
}

export default RegisterForm
